import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Coins, Gift as GiftIcon, CheckCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

export default function Gifts() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [balance, setBalance] = useState({ coin_balance: 0, gift_earnings: 0 });
  const [loading, setLoading] = useState(false);
  const [recipientId, setRecipientId] = useState("");

  const load = useCallback(async () => {
    const [pRes, gRes, bRes] = await Promise.all([
      fetch(`${API}/coins/packs`),
      fetch(`${API}/gifts/catalog`),
      fetch(`${API}/coins/balance`, { credentials: "include" }),
    ]);
    if (pRes.ok) setPacks((await pRes.json()).packs);
    if (gRes.ok) setCatalog((await gRes.json()).gifts);
    if (bRes.ok) setBalance(await bRes.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const buyPack = async (pack_id) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/coins/create-session`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack_id, origin_url: window.location.origin }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      window.location.href = d.url;
    } catch (e) { toast.error(e.message); setLoading(false); }
  };

  const sendGift = async (gift_id) => {
    if (!recipientId.trim()) { toast.error("Enter recipient user ID first"); return; }
    try {
      const r = await fetch(`${API}/gifts/send`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gift_id, to_user_id: recipientId.trim() }),
      });
      if (r.status === 402) { toast.error("Not enough coins! Buy a pack first."); return; }
      if (!r.ok) throw new Error((await r.json()).detail || "Failed");
      const d = await r.json();
      toast.success(`${d.gift_emoji} ${d.gift_label} sent to ${d.to_name}!`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-3xl">
        <div className="text-center mb-6">
          <GiftIcon className="w-14 h-14 mx-auto text-[#FFD700] mb-2" style={{ filter: "drop-shadow(0 0 15px #FFD700)" }} />
          <h1 className="text-3xl font-bold text-[#FFD700]" style={{ textShadow: "0 0 10px #FFD700" }} data-testid="gifts-title">Coins & Virtual Gifts</h1>
        </div>

        <div className="myspace-card mb-6 flex items-center justify-between" data-testid="balance-card">
          <div className="flex items-center gap-3">
            <Coins className="w-8 h-8 text-[#FFD700]" />
            <div>
              <p className="text-2xl font-bold text-[#00FFFF]">{balance.coin_balance.toLocaleString()}</p>
              <p className="text-xs text-[#99CCFF]">Available coins</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-[#00FF00]">{balance.gift_earnings.toLocaleString()}</p>
            <p className="text-xs text-[#99CCFF]">Gift earnings</p>
          </div>
        </div>

        <h2 className="text-xl font-bold text-[#FF66FF] mb-3 mt-6">Buy coin packs</h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {packs.map(p => (
            <button key={p.id} onClick={() => buyPack(p.id)} disabled={loading}
              className="myspace-card text-left hover:border-[#FFD700] disabled:opacity-50 transition" data-testid={`coin-pack-${p.id}`}
            >
              <p className="text-lg font-bold text-[#FFD700]">{p.label}</p>
              <p className="text-sm text-[#00FFFF] mt-1">${p.amount}</p>
              {p.bonus > 0 && <p className="text-xs text-[#00FF00] mt-1">+ {p.bonus} bonus coins</p>}
            </button>
          ))}
        </div>

        <h2 className="text-xl font-bold text-[#FF66FF] mb-3">Send a gift</h2>
        <div className="myspace-card mb-3">
          <label className="block text-xs text-[#99CCFF] mb-2">Recipient user ID</label>
          <input value={recipientId} onChange={(e) => setRecipientId(e.target.value)}
            placeholder="user_xxxxxxxxxxxx"
            className="w-full bg-[rgba(0,0,0,0.4)] border-2 border-[#6600CC] text-[#00FFFF] px-3 py-2 outline-none focus:border-[#FF00FF]"
            data-testid="gift-recipient-input"
          />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {catalog.map(g => (
            <button key={g.id} onClick={() => sendGift(g.id)}
              className="myspace-card flex flex-col items-center p-3 hover:border-[#FFD700] transition"
              data-testid={`gift-${g.id}`}
            >
              <span className="text-3xl">{g.emoji}</span>
              <p className="text-xs text-[#00FFFF] mt-1">{g.label}</p>
              <p className="text-xs text-[#FFD700]">{g.coins}🪙</p>
              <Send className="w-3 h-3 text-[#FF66FF] mt-1" />
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}

export function GiftsSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  const poll = useCallback(async (sid, attempt) => {
    if (attempt >= 6) { setStatus("timeout"); return; }
    try {
      const r = await fetch(`${API}/coins/status/${sid}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        if (d.payment_status === "paid") {
          setStatus("paid");
          if (refreshUser) await refreshUser();
          return;
        }
        if (d.status === "expired") { setStatus("expired"); return; }
      }
    } catch (e) { /* retry */ }
    setTimeout(() => poll(sid, attempt + 1), 2000);
  }, [refreshUser]);

  useEffect(() => { if (sessionId) poll(sessionId, 0); }, [sessionId, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <div className="myspace-card text-center max-w-md w-full">
        {status === "checking" && (<><div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-[#FFD700]">Crediting your coins...</p></>)}
        {status === "paid" && (<>
          <Coins className="w-20 h-20 mx-auto text-[#FFD700] mb-4" style={{ filter: "drop-shadow(0 0 20px #FFD700)" }} />
          <h2 className="text-3xl font-bold text-[#FFD700] mb-2" style={{ textShadow: "0 0 15px #FFD700" }} data-testid="gifts-success-title">Coins Added!</h2>
          <p className="text-[#00FFFF] mb-6">Your balance has been topped up.</p>
          <button onClick={() => navigate("/gifts")} className="myspace-btn-primary" data-testid="gifts-success-continue-btn">Send a gift</button>
        </>)}
        {(status === "timeout" || status === "expired") && (<><p className="text-[#FF6600] text-lg font-bold mb-4">Payment could not be verified</p><button onClick={() => navigate("/gifts")} className="myspace-btn">Back</button></>)}
      </div>
    </div>
  );
}
